/**
 * Why the run wrote no line for a stock cell — the platform's `uncovered_reason` in words
 * (bata-ui#302 E, platform ADR 6057). The codes are the platform's `UNCOVERED_REASONS`, in the
 * run's own pipeline order; a code this build has no words for is shown as the code itself, never
 * guessed. Pure — the reads are `./reads.ts`'s.
 */

import { PULL_REASON_CODES, pullReasonWords, pullReasonsUnreadable } from './pull-reasons';

/** A literal table, so every key is a reference the catalogue check sees. The words of the handover's two causes (`./pull-reasons.ts`) are the platform's. */
export const CAUSE_KEYS: Readonly<Record<string, string>> = {
  // ADR 6063 (platform): cause 0 — the stock day arrived without its DC or in-transit channel,
  // so the run refused its DC pulls and stated no need.
  stock_feed_incomplete: 'inseason.networkHealth.cause.stockFeedIncomplete',
  // ADR 6070 (platform): also cause 0 — the run refused its DC pulls because the market has no
  // ranging recency or ranged-cell rise limit in force, or because the cells it ranged rose past
  // that limit against the market's previous complete run.
  ranging_parameters_missing: 'inseason.networkHealth.cause.rangingParametersMissing',
  ranged_cells_rise: 'inseason.networkHealth.cause.rangedCellsRise',
  need_zero_in_transit: 'inseason.networkHealth.cause.needZeroInTransit',
  need_below_one_unit: 'inseason.networkHealth.cause.needBelowOneUnit',
  forecast_horizon_short: 'inseason.networkHealth.cause.forecastHorizonShort',
  'door_excluded:dec06': 'inseason.networkHealth.cause.doorExcludedDec06',
  'door_excluded:door_master': 'inseason.networkHealth.cause.doorExcludedDoorMaster',
  'door_excluded:on_order': 'inseason.networkHealth.cause.doorExcludedOnOrder',
  'door_excluded:door_full': 'inseason.networkHealth.cause.doorExcludedDoorFull',
  dc_pool_empty: 'inseason.networkHealth.cause.dcPoolEmpty',
  dc_pool_exhausted: 'inseason.networkHealth.cause.dcPoolExhausted',
  run_refused: 'inseason.networkHealth.cause.runRefused',
  not_selected_budget: 'inseason.networkHealth.cause.notSelectedBudget',
  not_selected_margin: 'inseason.networkHealth.cause.notSelectedMargin',
  door_not_posed: 'inseason.networkHealth.cause.doorNotPosed',
  case_round_to_zero: 'inseason.networkHealth.cause.caseRoundToZero',
  size_run_dropped: 'inseason.networkHealth.cause.sizeRunDropped',
  shipment_below_minimum: 'inseason.networkHealth.cause.shipmentBelowMinimum',
  // ADR 6188 (platform): the cell's drop, or its store's whole shipment, did not pay for its
  // transport (shipment, carton, pallet), so it was held; the pairs stay at the DC for next cycle.
  shipment_cost_held: 'inseason.networkHealth.cause.shipmentCostHeld',
  move_cap_deferred: 'inseason.networkHealth.cause.moveCapDeferred',
  size_split_zero: 'inseason.networkHealth.cause.sizeSplitZero',
  // ADR 6182 (platform): not a reason a cell has no line but why a ranged SIZE has no forecast, so
  // no target and no pull (`GET …/report/replenishment/unforecast`, `./unforecast.tsx`). The first
  // is worded on the window's weeks (`weeks`), the platform's `window_weeks`.
  no_sales_in_window: 'inseason.networkHealth.cause.noSalesInWindow',
  forecast_absent: 'inseason.networkHealth.cause.forecastAbsent',
};

/** The one sentence a handover cause reads as when the platform's words for it could not be read. */
const PULL_UNREADABLE_KEY = 'inseason.networkHealth.cause.pullReasonUnreadable';

/**
 * The run's order of the uncovered reasons: `CAUSE_KEYS`' own, with the handover's two causes in the places the run reaches them — the
 * unreadable ledger beside the other refusals of the day, the covered pairs before the DC pool's.
 */
export const CAUSE_ORDER: readonly string[] = (() => {
  const order = Object.keys(CAUSE_KEYS);
  order.splice(order.indexOf('ranged_cells_rise') + 1, 0, 'handover_ledger_unreadable');
  order.splice(order.indexOf('dc_pool_empty'), 0, 'covered_by_initial_allocation');
  return order;
})();

/**
 * The cause's words, or the platform's code when this build has none. `no_sales_in_window` is
 * worded on the window's weeks: the caller's `translate` carries `{ weeks }` (`./unforecast.tsx`).
 */
export function causeWords(code: string, translate: (key: string) => string): string {
  const key = CAUSE_KEYS[code];
  if (key === undefined) {
    if (!(PULL_REASON_CODES as readonly string[]).includes(code)) return code;
    // The platform's words; one sentence, never the code, when its read failed or it labels no such cause.
    return pullReasonsUnreadable() ? translate(PULL_UNREADABLE_KEY) : pullReasonWords(code) ?? translate(PULL_UNREADABLE_KEY);
  }
  const text = translate(key);
  return text === '' || text === key ? code : text;
}

// ── need_below_one_unit, told by the cell's position (platform ADR 6204) ──────

/**
 * The engine writes `need_below_one_unit` whenever the target less the position is under one pair,
 * which hides two situations: the store already holds enough (position above 0) or it is empty and
 * the forecast does not justify a pair (position 0 or less). The words below are the ONLY place the
 * two are named; the every-cell file, its summary and the cockpit's cell notes all take them from here.
 */
export const NEED_BELOW_ONE_UNIT = 'need_below_one_unit';

/** The position the run netted the target with (on hand + in transit + on order) is above nought. `null`: unknown. */
export function hasPosition(position: number | null | undefined): boolean {
  return position !== null && position !== undefined && position > 0;
}

/**
 * The cause's words, told by the cell's position where the cause is `need_below_one_unit` and the
 * position is known: «Non serve: ha già abbastanza» or «Non serve: domanda troppo bassa». Every
 * other cause, and that one with no position, is {@link causeWords}.
 */
export function causeWordsAt(code: string, translate: (key: string) => string, position: number | null | undefined): string {
  if (code !== NEED_BELOW_ONE_UNIT || position === null || position === undefined) return causeWords(code, translate);
  return translate(hasPosition(position) ? 'inseason.runCells.family.enough' : 'inseason.runCells.family.lowDemand');
}
